import { BusinessObjectBase } from './base';
import { packageDocSchema } from '@schemas/business-objects/package-doc';
import type { NpmPackage } from './npm-package';
import type { Id } from '../base-types';

export interface PackageDocData {
  readonly id: number;
  readonly version?: string;
  readonly path?: string;
  readonly title?: string;
  readonly content?: string;
  readonly npmPackageId: Id;
  readonly npmPackage?: NpmPackage;
}

export interface PackageDoc extends Readonly<PackageDocData> {}
type PackageDocCreateWithChildrenData = Partial<PackageDocData>;

export class PackageDoc extends BusinessObjectBase {
  static readonly entityName = 'PackageDoc' as const;
  static readonly plural = 'package-docs';
  static schema = packageDocSchema;

  // --- Static CRUD methods ---

  static async find(filter?: any): Promise<PackageDoc[]> {
    const url = `${this.baseUrl}/${this.plural}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const results = await this.get<any[]>(url, params);
    return results.map((data) => new PackageDoc(data));
  }

  static async findOne(filter?: any): Promise<PackageDoc | null> {
    const url = `${this.baseUrl}/${this.plural}/find-one`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return data ? new PackageDoc(data) : null;
  }

  static async findById(id: number, filter?: any): Promise<PackageDoc> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return new PackageDoc(data);
  }

  static async create(data: Omit<PackageDocData, 'id'>): Promise<PackageDoc> {
    const url = `${this.baseUrl}/${this.plural}`;
    const result = await this.post<any>(url, data);
    return new PackageDoc(result);
  }

  static async createMany(
    data: Omit<PackageDocData, 'id'>[],
  ): Promise<PackageDoc[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new PackageDoc(item));
  }

  static async createWithChildren(
    data: PackageDocCreateWithChildrenData,
  ): Promise<PackageDoc> {
    const url = `${this.baseUrl}/${this.plural}/create-with-children`;
    const result = await this.post<any>(url, data);
    return new PackageDoc(result);
  }

  static async createManyWithChildren(
    data: PackageDocCreateWithChildrenData[],
  ): Promise<PackageDoc[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many-with-children`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new PackageDoc(item));
  }

  static async updateById(
    id: number,
    updates: Partial<PackageDocData>,
  ): Promise<PackageDoc> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const result = await this.patch<any>(url, updates);
    return new PackageDoc(result);
  }

  static async deleteById(id: number): Promise<boolean> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    try {
      await this.del<void>(url);
      return true;
    } catch {
      return false;
    }
  }
}
